import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Search,
  ShieldAlert,
  RefreshCw,
  GitCompare,
  Ban,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { GetDataChecks, RunDataCheck } from "./apis/get";
import { DeactivateDataCheckRows, DeleteDataCheckRows } from "./apis/post";

interface CheckMeta {
  key: string;
  label: string;
  description: string;
  table: string;
}

interface RunResult {
  key: string;
  label: string;
  description: string;
  table: string;
  total: number;
  limit: number;
  offset: number;
  rows: Record<string, any>[];
}

// Columns shown first (in this order) when present; the rest follow.
const COL_ORDER = [
  "id",
  "user_id",
  "candidate_id",
  "male_name",
  "female_name",
  "male_age",
  "female_age",
  "age_gap",
  "male_income",
  "female_income",
  "income_gap",
  "match_score",
  "compatibility_level",
  "published",
  "male_maternal_gotra",
  "female_maternal_gotra",
  "male_gotra",
  "female_gotra",
  "source_service",
  "is_active",
  "created_at",
];

const PAGE_SIZE = 100;

const labelize = (k: string) =>
  k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const fmt = (v: any) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

export default function MatchDataChecks() {
  const [checks, setChecks] = useState<CheckMeta[]>([]);
  const [activeKey, setActiveKey] = useState<string>("");
  const [userId, setUserId] = useState("");
  const [result, setResult] = useState<RunResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await GetDataChecks();
      if (res?.status === "success" && Array.isArray(res.data)) {
        setChecks(res.data);
        if (res.data.length) setActiveKey(res.data[0].key);
      }
    })();
  }, []);

  const run = useCallback(
    async (key: string, off: number) => {
      if (!key) return;
      setLoading(true);
      setSelected(new Set());
      try {
        const res = await RunDataCheck(key, {
          user_id: userId.trim() || undefined,
          limit: PAGE_SIZE,
          offset: off,
        });
        if (res?.status === "success" && res.data) {
          setResult(res.data);
          setOffset(off);
        } else {
          setResult(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [userId],
  );

  // Run whenever the active check changes
  useEffect(() => {
    if (activeKey) run(activeKey, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  const activeMeta = checks.find((c) => c.key === activeKey);
  const rows = result?.rows || [];

  const columns: string[] = rows.length
    ? [
        ...COL_ORDER.filter((c) => c in rows[0]),
        ...Object.keys(rows[0]).filter((c) => !COL_ORDER.includes(c)),
      ]
    : [];

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPage) rows.forEach((r) => next.delete(r.id));
      else rows.forEach((r) => next.add(r.id));
      return next;
    });
  };

  const doAction = async (kind: "deactivate" | "delete") => {
    const ids = Array.from(selected);
    if (!ids.length || !activeMeta) return;
    const verb = kind === "delete" ? "PERMANENTLY DELETE" : "deactivate (is_active=false)";
    if (
      !window.confirm(
        `${kind === "delete" ? "⚠️ " : ""}${verb} ${ids.length} selected pair(s) from "${activeMeta.table}"?` +
          `\n\nThis also affects each pair's reverse match (B→A) in the same table.` +
          (kind === "delete" ? "\n\nThis cannot be undone." : ""),
      )
    )
      return;
    setBusy(true);
    try {
      const res =
        kind === "delete"
          ? await DeleteDataCheckRows(activeKey, ids)
          : await DeactivateDataCheckRows(activeKey, ids);
      if (res?.status === "success") {
        toast.success(res.message || "Done");
        await run(activeKey, offset);
      } else {
        toast.error(res?.message || res?.detail || "Action failed");
      }
    } catch (e: any) {
      toast.error(e?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const total = result?.total || 0;
  const showingFrom = total > 0 ? offset + 1 : 0;
  const showingTo = Math.min(offset + rows.length, total);
  const hasMore = offset + rows.length < total;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert className="text-amber-400" />
          <h1 className="text-2xl font-bold">Match Data Checks</h1>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          Curated data-quality checks over the match tables. Review flagged rows, then deactivate or delete them.
        </p>

        {/* Check selector */}
        <div className="flex flex-wrap gap-2 mb-4">
          {checks.map((c) => (
            <button
              key={c.key}
              onClick={() => setActiveKey(c.key)}
              className={`px-3 py-2 rounded-xl text-sm font-semibold border transition ${
                c.key === activeKey
                  ? "bg-indigo-600 border-indigo-500 text-white"
                  : "bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-600"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {activeMeta && (
          <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4 mb-4">
            <p className="text-sm text-slate-400">{activeMeta.description}</p>
            <p className="text-xs text-slate-600 mt-1 font-mono">target table: {activeMeta.table}</p>
            <div className="flex flex-wrap items-end gap-3 mt-3">
              <div className="flex flex-col">
                <label className="text-xs text-slate-500 mb-1">Filter by User ID (male side, optional)</label>
                <input
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && run(activeKey, 0)}
                  placeholder="U12345"
                  className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 w-56"
                />
              </div>
              <button
                onClick={() => run(activeKey, 0)}
                disabled={loading}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 rounded-lg px-4 py-2 text-sm font-semibold"
              >
                <Search size={16} />
                {loading ? "Running…" : "Run"}
              </button>
              <button
                onClick={() => run(activeKey, offset)}
                disabled={loading}
                className="flex items-center gap-2 border border-slate-700 hover:border-slate-500 rounded-lg px-3 py-2 text-sm"
              >
                <RefreshCw size={14} />
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* Summary + bulk actions */}
        {result && (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="text-sm text-slate-300">
              <span className="text-2xl font-bold text-amber-400">{total}</span>{" "}
              <span className="text-slate-500">rows flagged</span>
              {total > 0 && (
                <span className="text-slate-600 ml-2">
                  (showing {showingFrom}-{showingTo})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{selected.size} selected</span>
              <button
                onClick={() => doAction("deactivate")}
                disabled={busy || selected.size === 0}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold bg-amber-600/20 text-amber-300 border border-amber-600/40 hover:bg-amber-600/30 disabled:opacity-40"
              >
                <Ban size={14} />
                Deactivate
              </button>
              <button
                onClick={() => doAction("delete")}
                disabled={busy || selected.size === 0}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold bg-rose-600/20 text-rose-300 border border-rose-600/40 hover:bg-rose-600/30 disabled:opacity-40"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          </div>
        )}

        {/* Results table */}
        {result && (
          <div className="overflow-x-auto rounded-2xl border border-slate-800">
            {rows.length === 0 ? (
              <div className="p-10 text-center text-slate-500">No rows flagged for this check. 🎉</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-900/80 text-slate-400">
                  <tr>
                    <th className="px-3 py-2">
                      <input type="checkbox" checked={allOnPage} onChange={toggleAll} />
                    </th>
                    {columns.map((c) => (
                      <th key={c} className="text-left font-medium px-3 py-2 whitespace-nowrap">
                        {labelize(c)}
                      </th>
                    ))}
                    <th className="px-3 py-2 text-left font-medium">Compare</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className={`border-t border-slate-800/70 ${selected.has(r.id) ? "bg-indigo-500/5" : ""}`}
                    >
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                      </td>
                      {columns.map((c) => (
                        <td key={c} className="px-3 py-2 whitespace-nowrap text-slate-200">
                          {c === "is_active" ? (
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                                r[c]
                                  ? "bg-emerald-600/20 text-emerald-300 border-emerald-600/40"
                                  : "bg-rose-600/20 text-rose-300 border-rose-600/40"
                              }`}
                            >
                              {r[c] ? "Active" : "Inactive"}
                            </span>
                          ) : c === "created_at" ? (
                            new Date(r[c]).toLocaleString()
                          ) : c === "age_gap" || c === "income_gap" ? (
                            <span className="font-mono font-semibold text-amber-300">
                              {c === "income_gap" && typeof r[c] === "number"
                                ? r[c].toLocaleString()
                                : fmt(r[c])}
                            </span>
                          ) : (
                            fmt(r[c])
                          )}
                        </td>
                      ))}
                      <td className="px-3 py-2">
                        {r.user_id && r.candidate_id ? (
                          <Link
                            to={`/match-pair?user_id=${encodeURIComponent(r.user_id)}&candidate_id=${encodeURIComponent(r.candidate_id)}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-indigo-500/20 bg-indigo-500/10 px-2.5 py-1 text-[11px] font-semibold text-indigo-300 hover:bg-indigo-500/20"
                          >
                            <GitCompare size={12} />
                            Compare
                          </Link>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Pagination */}
        {result && total > PAGE_SIZE && (
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              onClick={() => run(activeKey, Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0 || loading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm disabled:opacity-40"
            >
              <ChevronLeft size={14} />
              Previous
            </button>
            <button
              onClick={() => run(activeKey, offset + PAGE_SIZE)}
              disabled={!hasMore || loading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm disabled:opacity-40"
            >
              Next
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
